import { useContext, useEffect, useMemo, useState } from 'react';
import { UnitContext } from '@src/core/Unit/context';
import { IRequiredIdentify } from '@src/models/analytics';
import { AppContext } from '../providers/App/AppContext';
import {
  IExperimentSettings,
  IExperimentUnitSettingsScenario,
  IExperimentUnitSettingsScenarioSelector,
  TFormFactor,
} from '@src/providers/App/models';
import { IUnit } from '@src/core/Unit/models';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { LOCAL_STORAGE_DISABLE_EXPERIMENTS } from '@src/api/ngApiClient/consts';

const isSuitableMatchEnv = (selector: IExperimentUnitSettingsScenarioSelector, unit: IUnit) => {
  if (selector.matchEnvs === undefined) {
    // Матчер не указан, значит сценарий подходит по этой проверке
    return true;
  }

  const matchEnvs = selector.matchEnvs || {};
  const selectorMatchEnvsKeys = Object.keys(matchEnvs);
  if (selectorMatchEnvsKeys.length > 0 && unit.env === undefined) {
    // Селекторы по env есть, а у юнита нет env вообще.
    // Смысла делать проход по массиву нет - мы точно ничего не найдем
    return false;
  }

  const unitEnv = unit.env || {};
  const unitEnvKeys = Object.keys(unitEnv);
  if (selectorMatchEnvsKeys.length > unitEnvKeys.length) {
    // Селекторов по env больше, чем самих env у юнита. Т.е. мы хотим найти больше, чем есть в наличие.
    // Смысла дальше искать нет - мы точно не сможем получить матч по всем искомым селекторам
    return false;
  }

  // тут _.isEqual не подойдет, так как у юнитов может быть больше env-ов, чем указано в селекторах
  for (const key of selectorMatchEnvsKeys) {
    if (unitEnv[key] === undefined) {
      // Нужный ключ в принципе отсутствует в env-е unit-а
      // Дальше искать нет смысла
      return false;
    }

    if (unitEnv[key] !== matchEnvs[key]) {
      // value у env-а отличается от искомого
      // Например искали formId=x, а пришел юнит с formId=y
      // Далее нам неважно совпадет ли что-то кроме, так как селектор работает по принципу "И"
      return false;
    }
  }

  // Если не нашли проблем, значит юнит подходит.
  // Лучше бы наверняка считать кол-во совпадений, чтобы по итогу проверить,
  // что оно совпало с selectorMatchEnvsKeys.length, но доп. переменную заводить не хочется
  return true;
};

const isSuitableFormFactor = (
  selector: IExperimentUnitSettingsScenarioSelector,
  formFactor: TFormFactor | undefined,
) => {
  if (selector.matchFormFactors === undefined) {
    // Матчер не указан, значит сценарий подходит по этой проверке
    return true;
  }

  if (!formFactor) {
    return false;
  }

  return selector.matchFormFactors.includes(formFactor);
};

/**
 * Проверяем на сколько подходит настройка юниту этого компонента.
 * Селекторы бывают самые простые - просто по имени юнита, а бывают более сложными, когда надо сравнивать еще и часть env.
 * Как пример более сложного поиска - эксперимент на FeedbackForm, когда надо делать замены для определенных formId
 * в рамках unitId=FeedbackForm
 */
const isSelectorSuitableSettings = (
  settingsScenario: IExperimentUnitSettingsScenario,
  unit: IUnit,
  formFactor: TFormFactor | undefined,
): boolean => {
  const selector = settingsScenario.selector;
  if (selector === undefined) {
    // Селектора нет вообще - это самый простой и самый общий случай
    // Когда нет никаких доп. условий кроме unit id для применения настроек
    return true;
  }

  return isSuitableMatchEnv(selector, unit) && isSuitableFormFactor(selector, formFactor);
};

/**
 * Первая версия ведения экспериментов для UI.
 * Эксперимент - это когда PM ставит задачу о том, что хочется поменять текст/цвет чего-то для
 * рекламной акции или вообще, при этом это надо сделать быстро, не затрагивая библиотеку компонентов,
 * так как часто эксперимент затрагивает тексты в хедере и футере.
 *
 * Система экспериментов работает за счет Unit подсистемы и ее требований к разметке всего и вся неизменяемыми id.
 *
 * По итогу работает так:
 * - для элемента c id (пока что Text, Button) вычисляется ближайший unit id сверху и
 *   в experimentsStore ищется инструкция для замены props этого элемента
 *
 *
 * todo: подумать как работать тут с переводами
 */
export const useExperiment = <T extends IRequiredIdentify>(props: T): T => {
  const { experimentsStore, isShowElemIdsInsteadChildren } = useContext(AppContext);
  const { getUnitPath } = useContext(UnitContext);
  const { breakpoint } = useContext(BreakpointsContext);

  // По умолчанию false, чтобы не промаргивали кнопки и тексты в случае,
  // когда у нас не спец тестирование, т.е. для основного количества случаев
  const [disableExp, setDisableExp] = useState<boolean>(false);

  useEffect(() => {
    // todo: Временная простая тема для отключения экспериментов
    // Для тестирования - эксперименты заваливают все тесты, пока надо сделать так, чтобы
    // Эксперименты не мешали
    const disable =
      typeof localStorage === 'undefined' ? 'false' : localStorage.getItem(LOCAL_STORAGE_DISABLE_EXPERIMENTS);
    setDisableExp(Boolean(disable && disable === 'true'));
  }, []);

  return useMemo(() => {
    const unitPath = getUnitPath();
    const firstUnit = unitPath && unitPath.length > 0 ? unitPath[0] : undefined;
    const unitId = firstUnit?.id || 'not_set';
    const elemPath = `${unitId}.${props.id}`;

    if (isShowElemIdsInsteadChildren) {
      // режим в AppContext, который на деве все строки перевода будет отдавать как пустоту
      return { ...props, children: elemPath };
    }

    if (experimentsStore === undefined || firstUnit === undefined) {
      // Вообще нет
      return props;
    }

    // todo: Пока всегда нулевой эксперимент. И это не константа!
    const experimentId = 0;

    const experimentSetting: IExperimentSettings = experimentsStore.experiments[experimentId];
    if (experimentSetting?.units === undefined) {
      return props;
    }
    const unitSetting = experimentSetting?.units[unitId];
    const unitSettingsScenarioList = unitSetting?.scenarios;
    if (unitSettingsScenarioList === undefined) {
      return props;
    }

    // todo: Временная простая тема для отключения экспериментов
    if (disableExp) {
      return props;
    }

    // Ищем первый подходящий сценарий и применяем результат для элемента
    for (const unitSettingsScenario of unitSettingsScenarioList) {
      const selectorSuitableSettings = isSelectorSuitableSettings(unitSettingsScenario, firstUnit, breakpoint);
      if (selectorSuitableSettings) {
        const elemSettings = unitSettingsScenario?.elems ? unitSettingsScenario?.elems[props.id] : undefined;
        if (elemSettings === undefined) {
          // Элемент описан, но настроек нет
          continue;
        }

        const elemStoreProps = elemSettings?.props;
        if (elemStoreProps) {
          // Как только нашли что-то, что можем применить - выходим
          return { ...props, ...elemStoreProps };
        }
      }
    }
    return props;
  }, [breakpoint, disableExp, experimentsStore, getUnitPath, isShowElemIdsInsteadChildren, props]);
};
